import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (username: string, password: string) =>
    api.post('/api/auth/login', { username, password }),
  signup: (username: string, password: string, invite_code: string) =>
    api.post('/api/auth/signup', { username, password, invite_code }),
  logout: () => api.post('/api/auth/logout'),
  getMe: () => api.get('/api/auth/me'),
};

// Scouts API
export const scoutsApi = {
  submitScout: (data: any) =>
    api.post('/api/scouts/submit', data),
  getTeamData: (teamNumber: number) =>
    api.get(`/api/scouts/teams/${teamNumber}`),
  getTeamMatchData: (teamNumber: number, matchNumber: number) =>
    api.get(`/api/scouts/teams/${teamNumber}/match/${matchNumber}`),
  getMatchData: (matchNumber: number) =>
    api.get(`/api/scouts/matches/${matchNumber}`),
  getAllData: (eventName?: string, allianceColor?: string) =>
    api.get('/api/scouts/all', { params: { event_name: eventName, alliance_color: allianceColor } }),
  getMatchSummary: (matchNumber: number) =>
    api.get(`/api/scouts/match-summary/${matchNumber}`),
};

// Analytics API
export const analyticsApi = {
  getTeamStats: (teamNumber: number) =>
    api.get(`/api/analytics/teams/${teamNumber}/stats`),
  getTeamRankings: (limit?: number) =>
    api.get('/api/analytics/teams/rankings', { params: { limit } }),
  getAllTeamStats: () =>
    api.get('/api/analytics/teams/all-stats'),
  calculateAllianceSynergy: (teamNumbers: number[]) =>
    api.post('/api/analytics/alliance/synergy', teamNumbers),
  getTeamTrend: (teamNumber: number, lastMatches?: number) =>
    api.get(`/api/analytics/teams/${teamNumber}/trend`, { params: { last_matches: lastMatches } }),
  compareTeams: (teamNumbers: number[]) =>
    api.post('/api/analytics/teams/compare', teamNumbers),
  getMatchPrediction: (matchNumber: number) =>
    api.get(`/api/analytics/match/${matchNumber}/prediction`),
};

// Alliance API
export const allianceApi = {
  analyzeAlliance: (teamNumbers: number[]) =>
    api.post('/api/alliance/analyze', teamNumbers),
  compareAlliances: (alliances: number[][]) =>
    api.post('/api/alliance/compare', alliances),
  optimizeAlliance: (availableTeams: number[], count?: number) =>
    api.post('/api/alliance/optimize', { available_teams: availableTeams, count }),
  predictMatchup: (redAlliance: number[], blueAlliance: number[]) =>
    api.post('/api/alliance/matchup-predict', { red_alliance: redAlliance, blue_alliance: blueAlliance }),
};

// Teams API
export const teamsApi = {
  getAllTeams: () =>
    api.get('/api/teams'),
  getTeamInfo: () =>
    api.get('/api/teams/info'),
  exportExcel: (filters?: any) =>
    api.post('/api/teams/export/excel', filters, {
      responseType: 'blob',
    }),
  exportCSV: (filters?: any) =>
    api.post('/api/teams/export/csv', filters, {
      responseType: 'blob',
    }),
  exportPDF: (teamNumber?: number, eventName?: string, allianceColor?: string) =>
    api.get('/api/teams/export/pdf', {
      params: { team_number: teamNumber, event_name: eventName, alliance_color: allianceColor },
      responseType: 'blob',
    }),
};

// Public API (no auth)
export const publicApi = {
  browse: () => api.get('/api/public/browse'),
};

// Health check
export const healthCheck = () =>
  api.get('/health');

export default api;
